// Organization invitations (`06` §3.3, `SC-F104`, `SC-F105`).
//
// The invitation link arrives by email and the recipient may have no account at
// all, so the landing page runs **anonymously**: [lookupInvite] takes no
// credential and the route is `pub`. That is why `/invite` is the one unguarded
// authenticated-product route in `router.ts`.
//
// The lookup must not become an existence oracle. "No such invite", "expired"
// and "already accepted" all answer the identical `404 not_found` (`SC-F104`,
// `SC-S105`), so the landing page has exactly one failure message and must not
// invent three.

import { del, getJSON, newIdempotencyKey, sendJSON, type Page } from "./http.js";
import type { Role } from "./orgs.js";

/** Invite is one pending invitation as a maintainer sees it. Row existence *is*
 * pending status: accept, revoke and expiry all delete the row. */
export interface Invite {
  id: string;
  email: string;
  role: Role;
  /** Attribution snapshot, so a removed maintainer's invitations stay legible. */
  invited_by_label: string;
  expires: string;
  created: string;
}

/** listInvites returns the organization's pending invitations (maintainer+). */
export function listInvites(orgId: string): Promise<Page<Invite>> {
  return getJSON<Page<Invite>>(`/v1/orgs/${encodeURIComponent(orgId)}/invites`);
}

/**
 * createInvite emails an invitation (`SC-F104`). The plaintext token exists
 * exactly once, in the mail; nothing in the API ever returns it again.
 *
 * Creation is braked by a per-actor **stock** budget rather than a rate-limit
 * label, because an exact label would have to contain `{orgId}` and labels are
 * compared against the literal request path (`SC-F105`, `06` §7). The honest
 * consequence, inherited from bino-cloud: a stock cap bounds *outstanding*
 * invitations, not send flow.
 */
export function createInvite(
  orgId: string,
  input: { email: string; role: Role },
): Promise<Invite> {
  return sendJSON<Invite>(`/v1/orgs/${encodeURIComponent(orgId)}/invites`, "POST", input, {
    idempotencyKey: newIdempotencyKey(),
  });
}

/**
 * resendInvite mails a fresh link for an invitation that is already pending —
 * the action `SC-S105` requires next to an expired row.
 *
 * There is no resend endpoint in `06` §3.3 and there does not need to be: row
 * existence *is* pending status, and `POST .../invites` lists no `conflict` or
 * `name_taken` among its failures, so re-creating for the same address replaces
 * the pending row and mints a new token. The consequences are worth stating
 * because they are user-visible: **the previous link stops working**, and the
 * resend consumes the same per-actor send budget as a new invitation
 * (`SC-S106`), so a `rate_limited` here means exactly what it means there.
 */
export function resendInvite(orgId: string, invite: Pick<Invite, "email" | "role">): Promise<Invite> {
  return createInvite(orgId, { email: invite.email, role: invite.role });
}

/** revokeInvite withdraws a pending invitation (`SC-F105`). */
export function revokeInvite(orgId: string, inviteId: string): Promise<void> {
  return del(
    `/v1/orgs/${encodeURIComponent(orgId)}/invites/${encodeURIComponent(inviteId)}`,
  );
}

/**
 * InviteLookup is what the anonymous landing page may know: which organization
 * is inviting, at which role, and which address the invitation was sent to. It
 * deliberately carries no member list, no instance names and no inviter email.
 */
export interface InviteLookup {
  org: { id: string; slug: string; name: string };
  email: string;
  role: Role;
  expires: string;
}

/**
 * lookupInvite resolves an emailed token **anonymously** — the recipient may not
 * have an account yet, and the page sends them to `/sign-in?next=…` itself.
 *
 * Every failure mode answers `404 not_found` on purpose. Do not try to
 * distinguish them in the UI: the uniformity is the anti-enumeration control.
 */
export function lookupInvite(token: string): Promise<InviteLookup> {
  return sendJSON<InviteLookup>("/v1/invites/lookup", "POST", { token }, { anonymous: true });
}

/**
 * acceptInvite joins the organization. **Session only** and absent from the
 * scoped-token route map: joining an organization is a person's act, not
 * automation's. Answers the new membership so the caller can route straight into
 * the organization it just joined.
 */
export function acceptInvite(token: string): Promise<{ org: string; role: Role }> {
  return sendJSON<{ org: string; role: Role }>(
    "/v1/invites/accept",
    "POST",
    { token },
    { idempotencyKey: newIdempotencyKey() },
  );
}
